import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";

const MS_BOOKINGS_COLOR = "#0078D4";

// ─── Exported nav helper ───────────────────────────────────────────────────────

export function BackToIntegrationLandingMicrosoftBookings() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="flex cursor-pointer items-center gap-2 text-slate-600"
      onClick={() => navigate("/integrations")}
    >
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
          clipRule="evenodd"
        />
      </svg>
      Back to Integrations
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface BookingsStatus {
  connected: boolean;
  businessId?: string;
  businessName?: string;
}

type TabId = "configuration" | "logs";

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  {
    name: "ms_bookings_get_business",
    label: "Get business info",
    group: "Business",
    description:
      "Get the Bookings business name, phone, email, address, and website."
  },
  {
    name: "ms_bookings_get_services",
    label: "Get available services",
    group: "Services",
    description:
      "List all bookable services with name, description, duration, and price."
  },
  {
    name: "ms_bookings_get_staff",
    label: "Get staff members",
    group: "Staff",
    description: "List all staff members available for appointments."
  },
  {
    name: "ms_bookings_check_availability",
    label: "Check availability",
    group: "Appointments",
    description:
      "Check available time slots for a specific service on a given date."
  },
  {
    name: "ms_bookings_create_appointment",
    label: "Create appointment",
    group: "Appointments",
    description:
      "Book an appointment for a customer — service, time, staff, and customer details."
  },
  {
    name: "ms_bookings_get_appointment",
    label: "Get appointment",
    group: "Appointments",
    description:
      "Look up an existing appointment by ID to check status and details."
  }
];

// ─── Configuration Tab ─────────────────────────────────────────────────────────

function ConfigurationTab({
  status,
  loading
}: {
  status: BookingsStatus | null;
  loading: boolean;
}) {
  const api = useApi();
  const [businessId, setBusinessId] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (status?.businessId) {
      setBusinessId(status.businessId);
    }
  }, [status]);

  async function handleSave() {
    if (!businessId.trim()) {
      setSaveError("Bookings Business ID is required.");
      return;
    }
    setSaveError(null);
    setSaveSuccess(false);
    setSaving(true);
    try {
      const resp = await api.admin.put("MicrosoftBookings/Config", {
        businessId: businessId.trim()
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setSaveError((d as any)?.error ?? "Save failed. Please try again.");
      } else {
        setSaveSuccess(true);
      }
    } catch (err) {
      console.error("MicrosoftBookings: save failed:", err);
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];

  return (
    <div className="space-y-4">
      {/* Prerequisites */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <div className="text-sm font-semibold text-blue-800 mb-1">
              Prerequisite: Microsoft 365 must be connected
            </div>
            <p className="text-xs text-blue-700">
              Microsoft Bookings piggybacks on your existing Microsoft 365
              connection. Before configuring Bookings, make sure your Microsoft
              365 account is connected on the{" "}
              <Link
                to="/integrations/microsoft-365"
                className="underline font-medium hover:text-blue-900"
              >
                Microsoft 365 integration page
              </Link>
              . Once connected, paste your Bookings Business ID below.
            </p>
          </div>
        </div>
      </div>

      {/* Status indicator */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="font-semibold text-slate-800 text-base">
              Connection Status
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Status of the Microsoft Bookings connection for this site.
            </p>
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <span
              className={`text-xs font-medium px-3 py-1 rounded-full ${
                status?.connected
                  ? "bg-green-100 text-green-700"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {status?.connected ? "Connected" : "Not Connected"}
            </span>
          )}
        </div>
        {status?.connected && status.businessName && (
          <div className="mb-5 rounded-lg bg-green-50 border border-green-200 p-3 text-xs text-green-800">
            Connected to: <strong>{status.businessName}</strong>
          </div>
        )}

        {/* Business ID field */}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Bookings Business ID{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": MS_BOOKINGS_COLOR } as React.CSSProperties}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={businessId}
              onChange={(e) => {
                setBusinessId(e.target.value);
                setSaveSuccess(false);
                setSaveError(null);
              }}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Find this in Microsoft Bookings: open your Bookings calendar in the
              Microsoft 365 admin portal, then copy the GUID from the URL
              (the part after{" "}
              <span className="font-mono">/bookings/calendar/</span>).
            </p>
          </div>

          {saveError && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs p-3">
              {saveError}
            </div>
          )}
          {saveSuccess && (
            <div className="rounded-lg bg-green-50 border border-green-200 text-green-700 text-xs p-3">
              Configuration saved successfully.
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: MS_BOOKINGS_COLOR }}
            >
              {saving ? "Saving..." : "Save Configuration"}
            </button>
          </div>
        </div>
      </div>

      {/* AI Tools */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="mb-4">
          <div className="font-semibold text-slate-800 text-base flex items-center gap-2">
            AI Tools
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                status?.connected
                  ? "bg-green-100 text-green-700"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {status?.connected ? `${AI_TOOLS.length} active` : `${AI_TOOLS.length} available`}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            All tools activate automatically once the Business ID is saved and
            Microsoft 365 is connected.
          </p>
        </div>
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                {group}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                  <div
                    key={tool.name}
                    className="rounded-lg border border-slate-100 bg-slate-50 p-3"
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                        style={{ backgroundColor: MS_BOOKINGS_COLOR }}
                      />
                      <div>
                        <div className="text-xs font-semibold text-slate-700">
                          {tool.label}
                        </div>
                        <div className="text-[10px] font-mono text-slate-400 mb-1">
                          {tool.name}
                        </div>
                        <div className="text-xs text-slate-500">
                          {tool.description}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function MicrosoftBookings() {
  const api = useApi();
  const [status, setStatus] = useState<BookingsStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("configuration");

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await api.admin.get("MicrosoftBookings/Status");
      if (resp.ok) {
        const data = await resp.json();
        setStatus(data);
      } else {
        setStatus({ connected: false });
      }
    } catch (err) {
      console.error("MicrosoftBookings: failed to load status:", err);
      setStatus({ connected: false });
    } finally {
      setStatusLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const TABS: { id: TabId; label: string }[] = [
    { id: "configuration", label: "Configuration" },
    { id: "logs", label: "Activity Log" }
  ];

  return (
    <div>
      {/* Header description */}
      <div className="bg-white rounded-2xl shadow-md p-5 mb-4 flex items-center gap-4">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: MS_BOOKINGS_COLOR }}
        >
          {/* Calendar icon */}
          <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
            <path d="M19 4h-1V2h-2v2H8V2H6v2H5C3.897 4 3 4.897 3 6v14c0 1.103.897 2 2 2h14c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2zm0 16H5V8h14v12z" />
            <path d="M7 10h5v5H7z" />
          </svg>
        </div>
        <div>
          <div className="font-semibold text-slate-800 text-base">
            Microsoft Bookings
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Let your AI bot check availability, book appointments, look up
            services and staff, and retrieve appointment details — powered by
            Microsoft Bookings. Requires an active Microsoft 365 connection.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-4">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`py-2 mr-4 text-sm cursor-pointer ${
              activeTab === tab.id
                ? "border-b-2 font-medium"
                : "text-slate-500"
            }`}
            style={
              activeTab === tab.id
                ? { borderColor: MS_BOOKINGS_COLOR, color: MS_BOOKINGS_COLOR }
                : {}
            }
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "configuration" && (
        <ConfigurationTab status={status} loading={statusLoading} />
      )}
      {activeTab === "logs" && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <IntegrationActivityLog integrationSlug="MicrosoftBookings" />
        </div>
      )}
    </div>
  );
}
